import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { CampusLernheft } from '@chancenwerk/campus-learning-content/entities/CampusLernheft.js';
import { CampusArea } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import type { ContextMenuItem } from '@chancenwerk/client/components/chancen-component-context-menu.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { CommentsAPI } from '@chancenwerk/comments/api/CommentsAPI.js';
import { Comment } from '@chancenwerk/comments/entities/Comment.js';
import { ContentResultEventlog } from '@chancenwerk/db/eventlogs/ContentResultEventlog.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { of, switchMap } from 'rxjs';
import '../components/campus-component-teacher-dashboard-lesson-list.js';
import { CampusRoutes } from '../routes.js';
import './campus-page-no-permission.js';

@customElement('campus-page-teacher-dashboard-student-profile')
export class CampusPageTeacherDashboardStudentProfile extends ChemistryLitElement {
  @property() userID = new RXController(this, '');

  @state() _student = new RXController(
    this,
    this.userID.observable.pipe(
      switchMap(id => {
        if (id.indexOf('employee_') === 0) {
          return of(null);
        }
        if (id.length > 0) {
          return subscribeByID(
            Student,
            id,
          )({
            _id: 1,
            guestAccount: 1,
            vorname: 1,
            nachname: 1,
            geburtstag: 1,
            kpfs: {
              school: {
                name: 1,
              },
            },
            levumiStatus: 1,
            lernhefteFinished: { _id: 1, title: 1 },
            lernhefteFinishedIDs: 1,
            lernhefteWorkingOn: { _id: 1, title: 1 },
            lernhefteWorkingOnIDs: 1,
            schoolInformation: {
              jahrgangsstufe: 1,
            },
            funding: {
              foerderbedarfLautEltern: 1,
            },
            legalGuardian1: {
              handynummer: 1,
            },
            legalGuardian2: {
              handynummer: 1,
            },
            levumiResults: ContentResultEventlog.allFields,
          });
        }
        return of(null);
      }),
    ),
  );

  @state() _employee = new RXController(
    this,
    this.userID.observable.pipe(
      switchMap(id => {
        if (id.indexOf('employee_') === -1) {
          return of(null);
        }
        if (id.length > 0) {
          return subscribeByID(
            Mitarbeiter,
            id.replace('employee_', ''),
          )({
            _id: 1,
            guestAccount: 1,
            vorname: 1,
            nachname: 1,
            geburtstag: 1,
            activeRelations: {
              predicate: { readableName: 1 },
              predicateID: 1,
              object: {
                school: {
                  name: 1,
                },
              },
            },
          });
        }
        return of(null);
      }),
    ),
  );

  private readonly size = new WindowSizeController(this);

  override styles = () => [
    {
      ':host': {
        // height: '100%',
        width: '100%',
      },
    },
  ];

  override render() {
    return permissionGuard(ChancenPermission.campus.VIEW_TEACHER_DASHBOARD, () => this._content(), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _content() {
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
        <cr-flex space="lg">
          <cr-h2> Teacher Dashboard </cr-h2>
        </cr-flex>

        ${this._profile()}
        <cr-flex w100 ${css({ height: '100px' })}></cr-flex>
      </cr-flex>
    `;
  }

  private _profile() {
    if (!this._student.value && !this._employee.value) {
      return html`<cr-loader></cr-loader>`;
    }

    return html`<cr-grid columns="${this._student.value ? '1fr 1fr 1fr' : '1fr 1fr'}" space="lg">
        ${this._studentProfileBox()} ${this._employeeProfileBox()} ${this._studentCommentBox()}
        ${this._studentHefteBox()}
      </cr-grid>
      <cr-grid columns="${this._student.value ? '1fr 1fr' : '1fr'}" space="lg" ${css({ alignItems: 'start' })}>
        ${this._studentLectureBox()} ${this._studentLevumiBox()}
      </cr-grid>`;
  }

  private _employeeProfileBox() {
    if (!this._employee.value) {
      return nothing;
    }

    return html`
      <cr-card padding="md" space="md" ${css({ width: '100%' })}>
        <cr-flex horizontal crossaxis-center space="md" ${css({ marginBottom: this.spacing.md })}>
          <cr-icon ${css({ fontSize: '2rem' })}>person_raised_hand</cr-icon>
          <cr-h3>${this._employee.value.vorname} ${this._employee.value.nachname}</cr-h3>
        </cr-flex>
        <cr-grid columns="1fr 3fr" space="lg">
          <cr-text bold ${css({ opacity: 0.5 })}> Schule: </cr-text>
          <cr-text>
            ${this._employee.value?.activeRelations.map(
              r =>
                html`
                  <cr-flex space="sm">
                    <cr-text bold> ${r.predicate.readableName}</cr-text>
                    <cr-text>${r.object?.school?.name}</cr-text>
                  </cr-flex>
                `,
            )}
          </cr-text>
        </cr-grid>
      </cr-card>
    `;
  }

  private _studentProfileBox() {
    if (!this._student.value) {
      return nothing;
    }

    return html`<cr-card padding="md" space="md" ${css({ width: '100%' })}>
      <cr-flex horizontal crossaxis-center space="md" ${css({ marginBottom: this.spacing.md })}>
        <cr-icon
          ${css({
            fontSize: '2rem',
          })}
          >person_raised_hand</cr-icon
        >
        <cr-h3
          ${css({
            color: (() => {
              if (baseConfig.get('DEPLOYMENT') === 'production') {
                return;
              }
              switch (this._student.value.levumiStatus) {
                case 'RED':
                  return 'red';
                case 'YELLOW':
                  return 'orange';
              }
            })(),
          })}
        >
          ${this._student.value.vorname} ${this._student.value.nachname}
          ${baseConfig.get('DEPLOYMENT') !== 'production' &&
          ['RED', 'YELLOW'].includes(this._student.value.levumiStatus ?? '')
            ? html`<cr-tooltip><cr-text>LEVUMI Tests stehen an!</cr-text></cr-tooltip>`
            : nothing}
        </cr-h3>
      </cr-flex>
      <cr-grid columns="max-content 1fr" space="lg">
        <cr-text bold ${css({ opacity: 0.5 })}> Schule: </cr-text>
        <cr-text> ${this._student.value.kpfs?.map(kpf => kpf.school?.name).join(', ')} </cr-text>
        <cr-text bold ${css({ opacity: 0.5 })}> Jahrgangsstufe: </cr-text>
        <cr-text> ${this._student.value.schoolInformation?.jahrgangsstufe} </cr-text>
        <cr-text bold ${css({ opacity: 0.5 })}> Geburtstag: </cr-text>
        <cr-text> ${this._student.value.geburtstag?.toFormat('dd.MM.yyyy')} </cr-text>
        <cr-text bold ${css({ opacity: 0.5 })}> Förderbedarf: </cr-text>
        <cr-text> ${this._student.value.funding?.foerderbedarfLautEltern?.join(', ') || 'Keinen hinterlegt'} </cr-text>
        <cr-text bold ${css({ opacity: 0.5 })}> Handynummer Erziehungsberechtigter: </cr-text>
        <cr-text>
          ${this._student.value.legalGuardian1?.handynummer ?? this._student.value.legalGuardian2?.handynummer}
        </cr-text>
        <cr-text bold ${css({ opacity: 0.5 })}> Teilnahme an Events: </cr-text>
        <cr-text> ... </cr-text>
      </cr-grid>
    </cr-card>`;
  }

  private _studentCommentBox() {
    return html`<cr-card padding="md" space="md">
      <chancen-component-card-title>
        Notizen
        <chancen-component-context-menu
          slot="right"
          .items=${[
            {
              icon: 'add',
              label: 'Kommentar hinzufügen',
              action: async () => {
                if (!this._student.value?._id && !this._employee.value?._id) {
                  return;
                }

                const comment = await ChromiumDialog.showPopup<string>({
                  content: (resolve, reject, dialogElement) => {
                    let comment = '';
                    return html`<cr-flex space="md" padding="md">
                      <cr-textarea
                        ${css({ width: '400px' })}
                        @value-picked=${(e: ValueEvent<string>) => (comment = e.detail.value)}
                      ></cr-textarea>
                      <cr-button @click=${() => resolve(comment)}>Speichern</cr-button>
                    </cr-flex>`;
                  },
                });

                try {
                  await CommentsAPI.create(
                    `TEACHER_DASHBOARD_NOTE_${this._student.value?._id ?? this._employee.value?._id}`,
                    comment,
                  )();
                } catch (e: any) {
                  ChromiumSnackbar.show(e.message);
                }
              },
            },
          ] satisfies ContextMenuItem[]}
        ></chancen-component-context-menu>
      </chancen-component-card-title>

      <cr-data-view
        .options=${{
          class: Comment,
          view: new View({
            filter: new AdornisFilter({
              sortingOrder: ['asc'],
              aspects: [
                new StringAspect({
                  fieldName: 'roomID',
                  mode: 'equals',
                  value: `TEACHER_DASHBOARD_NOTE_${this._student.value?._id ?? this._employee.value?._id}`,
                }),
              ],
            }),
          }),
          onlyShowLastTranslation: true,
          customColumns: [
            {
              fieldName: 'createdAt',
              columnName: 'von wann',
              columnWidth: 'max-content',
              isSortable: false,
            },
            {
              fieldName: 'text',
              columnName: 'Kommentar',
              isSortable: false,
              getFieldMapping: e => {
                const edit = async () => {
                  await ChromiumDialog.showPopup<void>({
                    content: (resolve, reject, dialogElement) => {
                      let comment = e.text;
                      return html`<cr-flex space="md" padding="md">
                        <cr-textarea
                          ${css({ width: '400px' })}
                          .value=${comment}
                          @value-picked=${(e: ValueEvent<string>) => (comment = e.detail.value)}
                        ></cr-textarea>
                        <cr-flex horizontal space="md">
                          <cr-button
                            @click=${async () => {
                              e.text = comment;
                              await e.save();
                              resolve();
                            }}
                            >Speichern</cr-button
                          >
                          <cr-button
                            tone="error"
                            @click=${async () => {
                              await e.remove();
                              resolve();
                            }}
                            >Löschen</cr-button
                          >
                        </cr-flex>
                      </cr-flex>`;
                    },
                  });
                };

                return html`<cr-text @click=${async () => edit()}>${e.text}</cr-text>`;
              },
            },
            {
              fieldName: 'user',
              columnName: 'Autor',
              selectionSet: {
                user: {
                  vorname: 1,
                  nachname: 1,
                },
              },
              getFieldMapping: e => `${e.user?.vorname} ${e.user?.nachname}`,
              isSortable: false,
            },
          ],
        } satisfies DataViewOptions<typeof Comment>}
      ></cr-data-view>
    </cr-card>`;
  }

  private _studentHefteBox() {
    if (this._employee.value) {
      return nothing;
    }

    return html`<cr-card padding="md" space="md">
      <chancen-component-card-title> Lernhefte in Bearbeitung </chancen-component-card-title>
      <x-entity-picker
        clearable
        .options=${{
          class: CampusLernheft,
          selectionSet: { title: 1 },
          filter: new AdornisFilter({
            searchFields: ['title'],
          }),
        }}
        @value-picked=${async (e: ValueEvent<Maybe<string>>) => {
          if (
            !this._student.value ||
            !e.detail.value ||
            this._student.value.lernhefteWorkingOnIDs?.includes(e.detail.value) ||
            this._student.value.lernhefteFinishedIDs?.includes(e.detail.value)
          ) {
            return;
          }

          if (!this._student.value.lernhefteWorkingOnIDs) {
            this._student.value.lernhefteWorkingOnIDs = [];
          }

          this._student.value.lernhefteWorkingOnIDs.push(e.detail.value);
          await this._student.value.save();
        }}
      ></x-entity-picker>

      ${this._student.value?.lernhefteWorkingOn?.map(heft => {
        return html`<cr-flex
          crossaxis-center
          horizontal
          space-between
          space="md"
          padding="sm"
          ${css({ backgroundColor: this.colors.neutral.shade(100), borderRadius: this.sizes.borderRadiusSecondary })}
        >
          <cr-text>${heft.title}</cr-text>
          <cr-flex horizontal space="sm">
            <cr-button
              mode="icon"
              icon="check_circle"
              @click=${async () => {
                if (!this._student.value?.lernhefteWorkingOnIDs) {
                  return;
                }

                this._student.value.lernhefteFinishedIDs = this._student.value.lernhefteFinishedIDs ?? [];
                this._student.value.lernhefteFinishedIDs.push(heft._id);
                this._student.value.lernhefteWorkingOnIDs = this._student.value.lernhefteWorkingOnIDs.filter(
                  id => id !== heft._id,
                );
                await this._student.value.save();
              }}
            ></cr-button>
            <cr-button
              mode="icon"
              icon="delete"
              tone="error"
              @click=${async () => {
                if (!this._student.value?.lernhefteWorkingOnIDs) {
                  return;
                }

                this._student.value.lernhefteWorkingOnIDs = this._student.value.lernhefteWorkingOnIDs.filter(
                  id => id !== heft._id,
                );
                await this._student.value.save();
              }}
            ></cr-button>
          </cr-flex>
        </cr-flex>`;
      })}

      <chancen-component-card-title> Beendete Lernhefte </chancen-component-card-title>
      ${this._student.value?.lernhefteFinished?.map(heft => {
        return html`<cr-flex
          crossaxis-center
          horizontal
          space-between
          space="md"
          padding="sm"
          ${css({
            backgroundColor: this.colors.tone.success.shade(100),
            borderRadius: this.sizes.borderRadiusSecondary,
          })}
        >
          <cr-text>${heft.title}</cr-text>
          <cr-flex horizontal space="sm">
            <cr-button
              mode="icon"
              icon="close"
              @click=${async () => {
                if (!this._student.value?.lernhefteWorkingOnIDs) {
                  return;
                }

                this._student.value.lernhefteFinishedIDs = this._student.value.lernhefteFinishedIDs?.filter(
                  id => id !== heft._id,
                );
                this._student.value.lernhefteWorkingOnIDs = this._student.value.lernhefteWorkingOnIDs ?? [];
                this._student.value.lernhefteWorkingOnIDs.push(heft._id);
                await this._student.value.save();
              }}
            ></cr-button>
            <cr-button
              mode="icon"
              icon="delete"
              tone="error"
              @click=${async () => {
                if (!this._student.value?.lernhefteWorkingOnIDs) {
                  return;
                }

                this._student.value.lernhefteFinishedIDs = this._student.value.lernhefteFinishedIDs?.filter(
                  id => id !== heft._id,
                );
                await this._student.value.save();
              }}
            ></cr-button>
          </cr-flex>
        </cr-flex>`;
      })}
    </cr-card>`;
  }

  private _studentLectureBox() {
    return html`<cr-card padding="md" space="md">
      <chancen-component-card-title>Bearbeitete Lektionen</chancen-component-card-title>
      <campus-component-teacher-dashboard-lesson-list
        .student=${this._student.value ?? this._employee.value}
        .area=${this._student.value ? CampusArea.STUDENTS : CampusArea.EMPLOYEES}
      ></campus-component-teacher-dashboard-lesson-list>
    </cr-card>`;
  }

  private _studentLevumiBox() {
    if (this._employee.value) {
      return nothing;
    }

    const results = this._student.value?.levumiResults ?? [];

    // We want to show the latest 5 results
    const filteredResults = results.filter(l => l.levumiResultInformation?.levumiLevel).slice(0, 5);

    return html`<cr-card padding="md" space="md">
      <chancen-component-card-title>Die letzten CHANCENkurve Tests</chancen-component-card-title>
      ${repeat(
        filteredResults ?? [],
        result => result._id,
        result => {
          return html`<cr-flex
            padding="md"
            space="md"
            ${css({
              backgroundColor: this.colors.neutral.shade(100),
              borderRadius: this.sizes.borderRadiusSecondary,
            })}
          >
            <chancen-component-card-title>
              LEVUMI
              ${{
                math: 'MATHE',
                german: 'DEUTSCH',
                number_dictation: 'ZAHLENDIKTAT',
              }[result.levumiResultInformation.levumiLevel?.subject ?? '']}
            </chancen-component-card-title>
            <cr-grid columns="max-content 1fr" space="md">
              <cr-text bold ${css({ opacity: 0.5 })}> Datum: </cr-text>
              <cr-text> ${DateTime.fromJSDate(result.createdAt ?? new Date()).toFormat('dd.MM.yyyy HH:mm')} </cr-text>
              <cr-text bold ${css({ opacity: 0.5 })}> Levumi-Level: </cr-text>
              <cr-text> ${result.levumiResultInformation.levumiLevel?.level} </cr-text>
              ${result.levumiResultInformation.levumiMathCategory
                ? html` <cr-text bold ${css({ opacity: 0.5 })}> Levumi-Kategorie: </cr-text>
                    <cr-text> ${result.levumiResultInformation.levumiMathCategory} </cr-text>`
                : nothing}
              <cr-text bold ${css({ opacity: 0.5 })}> Genauigkeit: </cr-text>
              <cr-text> ${((result.levumiResultInformation.correctlyAnsweredPercent ?? 0) * 100).toFixed(0)}% </cr-text>
              <cr-text bold ${css({ opacity: 0.5 })}> Korrekt beantwortet/insg. beantwortet: </cr-text>
              <cr-text>
                ${result.levumiResultInformation.correctlyAnswered} / ${result.levumiResultInformation.overallAnswered}
              </cr-text>
            </cr-grid>
            <cr-button
              href="${CampusRoutes.diagnostics}/${this._student.value?._id || 'invalid_id'}?subject=${result
                .levumiResultInformation.levumiLevel?.subject}&level=${result.levumiResultInformation.levumiLevel
                ?.level}&category=${result.levumiResultInformation.levumiMathCategory}&testId=${result._id}"
              target="_blank"
              >Zu den Ergebnissen</cr-button
            >
          </cr-flex>`;
        },
      )}
    </cr-card>`;
  }
}
